import { useRef } from 'react';
import { Box, Flex } from '@rebass/grid';
import animateScrollTo from 'animated-scroll-to';

import { Heading3, Text1, variables } from 'app/shared/ui';
import { HtmlHeader } from 'app/shared/components/HtmlHeader/HtmlHeader';
import { useStyledTheme } from 'app/shared/hooks/useStyledTheme';
import { GridContainerSt } from 'app/shared/modules/FDL/tradein/components/SectionContents/HowDoesItWork/HowDoesItWork.css';
import { GridItem } from 'app/shared/modules/FDL/tradein/components/SectionContents/HowDoesItWork/GridItem/GridItem';
import { SellMyCarOnline } from 'app/shared/modules/FDL/tradein/pages/Carsale24/components/SellMyCarOnline';
import { generateCarsale24IframeSrc } from 'app/shared/modules/FDL/tradein/helpers';
import { AccordionItem } from 'app/shared/components/Accordion/AccordionItem/AccordionItem';
import { Accordion } from 'app/shared/components/Accordion/Accordion';
import { BASE_URL } from 'app/config';

import { ConnectedContacts } from '../../components/Contacts/ConnectedContacts';
import { ImageWithListItems } from '../../components/SectionContents/ImageWithListItems/ImageWithListItems';
import { Section } from '../../components/Section/Section';
import { FullContainerSt } from '../IndexPage.css';
import { AboutCarSale24 } from '../../components/SectionContents/AboutCarSale24';

import { messages, faqEntries } from './constants';
import { ICarSale24PageProps } from './CarSale24Page.types';
import {
  PassiveTextSt,
  PassiveLinkSt,
  FaqContentSt,
} from './CarSale24Page.css';

const {
  height: { topNavBar },
} = variables;

const faqJson = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqEntries.map((faqEntry) => ({
    '@type': 'Question',
    name: faqEntry.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faqEntry.answer,
    },
  })),
};

export const Carsale24Page = ({
  location,
  isFlag244ShowCarsale24Form = false,
}: ICarSale24PageProps): JSX.Element => {
  const sectionRef = useRef<HTMLElement>(null);
  const { colors } = useStyledTheme();
  const iframeSrc = generateCarsale24IframeSrc(location);

  const handleClick = async () => {
    if (sectionRef?.current) {
      await animateScrollTo(sectionRef.current, {
        verticalOffset: -1 * topNavBar,
        speed: 900,
      });
    }
  };

  return (
    <>
      <HtmlHeader
        title="Auto verkaufen: Gebrauchtwagen online verkaufen | heycar"
        description="Verkaufe dein Auto online mit heycar! Dein Gebrauchtwagen geht an den Meistbietenden. Mit persönlicher Beratung und geprüften Käufern."
        index="index,follow"
        url={`${BASE_URL as string}${location.pathname}`}
        currentUrl={`${BASE_URL as string}${location.pathname}${
          location.search
        }`}
      >
        <script type="application/ld+json">{JSON.stringify(faqJson)}</script>
      </HtmlHeader>
      <FullContainerSt mt={64}>
        <Section
          py={[32, 48, 56, 64]}
          px={[12, 28, 36, 0]}
          backgroundColor={colors.sunbeanLight}
          $maxWidth={1168}
        >
          <SellMyCarOnline
            iframeSrc={iframeSrc}
            isFlag244ShowCarsale24Form={isFlag244ShowCarsale24Form}
          />
        </Section>
        <Section>
          <Box mb={[44, 56, 56, 64]}>
            <Heading3 fontSize={[32, 32, 40]} fontWeight="bold">
              So einfach funktioniert’s
            </Heading3>
          </Box>

          <GridContainerSt>
            <GridItem iconName="vehicle-with-checkmark">
              <Flex alignItems={['center', 'start']}>
                <span>
                  1. Gib die <strong>Fahrzeugdaten und den Zustand</strong>{' '}
                  deines Fahrzeugs an.
                </span>
              </Flex>
            </GridItem>

            <GridItem iconName="listing">
              <Flex alignItems={['center', 'start']}>
                <span>
                  2. Carsale24 überprüft deine Fahrzeugdaten und erstellt ein{' '}
                  <strong>Inserat für dein Fahrzeug</strong>.
                </span>
              </Flex>
            </GridItem>

            <GridItem iconName="trophy">
              <Flex alignItems={['center', 'start']}>
                <span>
                  3. Du erhältst <strong>unverbindliche Kaufangebote</strong>,
                  die noch einmal für dich <strong>nachverhandelt</strong>{' '}
                  werden.
                </span>
              </Flex>
            </GridItem>

            <GridItem iconName="deal">
              <Flex alignItems={['center', 'start']}>
                <span>
                  4. Wähle das beste Angebot für dich und vereinbare einen{' '}
                  <strong>gemeinsamen Termin</strong>.
                </span>
              </Flex>
            </GridItem>
          </GridContainerSt>
          <PassiveTextSt>
            Du hast noch offene Fragen? Schaue doch mal in den{' '}
            <PassiveLinkSt onClick={handleClick}>FAQs</PassiveLinkSt> vorbei.
          </PassiveTextSt>
        </Section>

        <Section backgroundColor={colors.sunbeanSuperLight}>
          <ImageWithListItems
            imageSrc="assets/tradein/hero_cs_1280.webp"
            title={messages.title}
            description={messages.description}
            listItems={[messages.item1, messages.item2, messages.item3]}
            variant="secondary"
          />
        </Section>

        <Section>
          <Box ref={sectionRef} mb={[44, 56, 56, 64]}>
            <Heading3 fontSize={[32, 32, 40]} fontWeight="bold">
              Deine Fragen beantwortet
            </Heading3>
          </Box>
          <FaqContentSt>
            <Accordion isMultiOpen hasFullWidthContent>
              {faqEntries.map((faqEntry, index) => (
                <AccordionItem
                  key={`faw_${index}`}
                  noMaxHeight
                  title={faqEntry.question}
                >
                  <div className="one">
                    <Text1>{faqEntry.answer}</Text1>
                  </div>
                </AccordionItem>
              ))}
            </Accordion>
          </FaqContentSt>
        </Section>

        <Section
          backgroundColor={colors.sunbeanSuperLight}
          py={[56, 64, 80, 96]}
        >
          <AboutCarSale24 />
        </Section>

        <Section>
          <ConnectedContacts />
        </Section>
      </FullContainerSt>
    </>
  );
};
